import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowParameterValueErrorType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class ExtractRaster extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'carto.extractraster',
        group: 'Raster Operations',
        title: 'Get Values from Raster',
        description: `
        This component allows obtaining values from a raster table. The values included in the output can be determined by a vector data source input.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Raster table'
        },
        {
          name: 'vector',
          type: WorkflowComponentParamType.InputTable,
          title: 'Vector table',
          optional: true
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'vector',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'columns',
          type: WorkflowComponentParamType.Column,
          title: 'Raster properties',
          parent: 'source',
          dataType: [ColumnDataType.Binary],
          mode: WorkflowComponentParameterMode.Multiple,
          optional: true
        },
        {
          name: 'addcustomexpression',
          type: WorkflowComponentParamType.Boolean,
          title: 'Add custom expression',
          optional: true,
          default: false
        },
        {
          name: 'expression',
          type: WorkflowComponentParamType.StringSql,
          title: '',
          optional: true,
          showIf: [{ parameter: 'addcustomexpression', value: true }],
          placeholder: 'band_1 + band_2 AS band_sum'
        },
        {
          name: 'addpixelcolumn',
          type: WorkflowComponentParamType.Boolean,
          title: 'Add pixel column',
          optional: true,
          default: false,
          advanced: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams) {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const prepParams = this._prepareParameters(params)
    const sourceTable =
      codeType === WorkflowCodeType.StoredProcedure && connection.type === Provider.BigQuery
        ? `${connection.tempLocation}.${tempTableName(componentName, prepParams.source as string)}`
        : (prepParams.source as string)
    const writeSourceTable =
      codeType === WorkflowCodeType.StoredProcedure && connection.type === Provider.BigQuery
        ? connection.getCreateTempTableQuery(
            sourceTable,
            `SELECT * FROM ${connection.quoteName(prepParams.source as string)}`,
            WorkflowCodeType.Full,
            null
          )
        : ''
    let expression = ((prepParams.columns as string[]) || []).join(', ')
    if (prepParams.expression && expression) {
      expression = `${expression},${prepParams.expression}`
    } else if (prepParams.expression) {
      expression = prepParams.expression as string
    }
    expression = expression.replace(/\n/g, '')
    const tempOutputTable = `${connection.tempLocation}.${tempTableName(componentName, expression)}`
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    if (!schemas) {
      return null
    }

    let inputQuery = connection.type === Provider.DatabricksRest ? "''" : 'NULL'
    if (prepParams.vector) {
      const vectorSchema = schemas.input['vector']
      if (!vectorSchema) {
        return null
      }

      let nonGeomCols = ''
      if (connection.type === Provider.DatabricksRest) {
        nonGeomCols = (vectorSchema as TableSchema)
          .filter((c) => c.name !== prepParams.geo)
          .map((c) => c.name)
          .join(', ')
      } else {
        nonGeomCols = (vectorSchema as TableSchema)
          .filter((c) => c.type !== ColumnDataType.Geography)
          .map((c) => c.name)
          .join(', ')
      }
      inputQuery = `'SELECT ${prepParams.geo} AS geom${
        nonGeomCols ? `, ${nonGeomCols}` : ''
      } FROM ${connection.quoteName(prepParams.vector as string)}'`
    }

    let query = ''
    const options = `{"include_pixel": ${JSON.stringify(prepParams.addpixelcolumn as boolean)}}`

    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Snowflake: {
        query = dedent`
          BEGIN
            ${writeSourceTable}
            DROP TABLE IF EXISTS ${connection.quoteName(
              storedProc && connection.type === Provider.BigQuery ? tempOutputTable : outputTablename
            )};
            CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.RASTER_VALUES(
                '${sourceTable}',
                ${inputQuery},
                '${expression}',
                '${storedProc && connection.type === Provider.BigQuery ? tempOutputTable : outputTablename}',
                '${options}'
            );
            ${
              storedProc && connection.type === Provider.BigQuery
                ? dedent`
                  CREATE TEMP TABLE ${connection.quoteName(outputTablename)} AS
                    SELECT *
                    FROM ${connection.quoteName(tempOutputTable)};
                  DROP TABLE ${connection.quoteName(tempOutputTable)};
                  DROP TABLE ${connection.quoteName(sourceTable)};`
                : ''
            }
          END;
        `
        break
      }
      case Provider.DatabricksRest: {
        query = dedent`
          spark.read.format("com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource").option(
            "sql",
            """
            CALL_CARTO carto_un.carto.RASTER_VALUES(
              '${prepParams.source as string}',
              ${inputQuery},
              '${expression}',
              '${outputTablename}',
              '${options}'
            );
            """
          ).load().collect();`
        break
      }
    }

    query = connection.wrapCodeInIfTableExistsBlock(query, [outputTablename], codeType)

    if (connection.type === Provider.DatabricksRest) {
      query = dedent`
      ${query}
      ${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(connection.optimizationStatsQuery(outputTablename))}
    `
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (resolvedParamValues.columns == null && resolvedParamValues.expression == null) {
      problems.push({
        parameter: 'expression',
        type: WorkflowParameterValueErrorType.MissingValue,
        message: 'At least one of "expression" and "columns" parameters must be specified'
      })
    }

    return problems
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Snowflake:
        return '1.0.0'
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    const prepParams = this._prepareParameters(params)
    const addPixelColumn = prepParams.addpixelcolumn as boolean
    let schema
    if (connection.type === Provider.DatabricksRest) {
      schema = columns.vector.filter((c) => c.name !== prepParams.geo)
    } else {
      schema = columns.vector.filter((c) => c.type !== ColumnDataType.Geography)
    }

    let expression
    if (prepParams.columns) {
      expression = ((prepParams.columns as string[]) || []).join(',')
    }
    if (prepParams.expression && expression) {
      expression = `${expression},${prepParams.expression}`
    } else if (prepParams.expression) {
      expression = prepParams.expression as string
    }
    expression = expression.replace(/\n/g, '')

    const expressionCols = expression.split(',').map((x) => x.split(' ').pop())

    for (const col of expressionCols) {
      schema.push({
        name: col,
        type: ColumnDataType.Number
      })
    }
    if (addPixelColumn) {
      schema.push({
        name: 'pixel',
        type: ColumnDataType.Number
      })
    }

    return schema
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake]
  }
}

export { ExtractRaster }
